import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";

import { useSignalR } from "../../contexts/SignalRContext";
import {
  IProductionDashboardAssembly,
  IProductionDashboardBay,
  IProductionDashboardBayVehicle,
} from "../../models/ProductionDashboard";
import { assemblyElapsedSeconds, formatAssemblyDuration } from "../../utils/assemblyElapsed";
import { assemblySteps, completedAssemblyNames, displayedAssemblies } from "../../utils/assemblySteps";
import { estimatedRemainingSeconds, formatRemaining } from "../../utils/estimatedRemaining";
import { bayInProgressVehicles, bayState, vehicleState } from "../../utils/bayCardState";
import classNames from "../../utils/classNames";
import Avatar from "../user/Avatar";
import { BAY_STATE_DARK, bayStateLabel } from "./bayStateStyles";

interface TvBayTileProps {
  bay: IProductionDashboardBay;
  assemblies: IProductionDashboardAssembly[];
  now: DateTime;
  busyVehicleIds?: Set<string>;
  hideNonAssembly?: boolean;
}

const vehicleName = (v: { combinedMake: string | null; combinedModel: string | null }) =>
  [v.combinedMake, v.combinedModel].filter(Boolean).join(" ");

function TvVehicle({
  vehicle,
  assemblies,
  now,
  compact,
  hideNonAssembly,
}: {
  vehicle: IProductionDashboardBayVehicle;
  assemblies: IProductionDashboardAssembly[];
  now: DateTime;
  compact: boolean;
  hideNonAssembly: boolean;
}) {
  const { t } = useTranslation("production");
  const { activeJobSessionsByVehicle } = useSignalR();
  const activeJobUsers = activeJobSessionsByVehicle[vehicle.id] ?? [];
  const state = vehicleState(vehicle, now);
  const s = BAY_STATE_DARK[state];
  const shown = { ...vehicle, assemblies: displayedAssemblies(vehicle, hideNonAssembly) };
  const steps = assemblySteps(shown, assemblies);
  const doneNames = completedAssemblyNames(shown);
  const remainingSeconds = estimatedRemainingSeconds(shown.assemblies, now);

  return (
    <div className="flex flex-col gap-3 flex-1 min-h-0">
      <div className="flex items-center gap-3 min-w-0">
        <span className="rounded bg-yellow-300 px-2.5 text-2xl font-bold tracking-wider text-gray-900 whitespace-nowrap">
          {vehicle.vrm || t("No VRM")}
        </span>
        <span className="text-lg text-gray-300 truncate">
          {vehicleName(vehicle) || vehicle.stockNumber || "-"}
        </span>
      </div>
      <div className="text-lg font-semibold text-gray-200 truncate">
        {vehicle.currentAssemblyName ?? t("No assembly")}
        {vehicle.assemblyCount > 0 && vehicle.currentAssemblyIndex && (
          <span className="text-gray-400 font-normal">
            {" "}
            · {vehicle.currentAssemblyIndex} / {vehicle.assemblyCount}
          </span>
        )}
      </div>
      <div className="flex items-baseline gap-2.5">
        <span
          className={classNames(
            "font-semibold leading-none tabular-nums",
            compact ? "text-5xl" : "text-7xl",
            s.timer
          )}
        >
          {formatAssemblyDuration(assemblyElapsedSeconds(vehicle, now))}
        </span>
        {vehicle.currentAssemblyAverageMinutes ? (
          <span className="text-lg text-gray-400">/ {vehicle.currentAssemblyAverageMinutes}m</span>
        ) : null}
        {remainingSeconds !== null && (
          <span className="ml-auto text-lg text-gray-300 tabular-nums whitespace-nowrap">
            {t("{{time}} left", { time: formatRemaining(remainingSeconds) })}
          </span>
        )}
      </div>
      {doneNames.length > 0 && (
        <div className="text-base text-gray-400 truncate">
          <span className="text-emerald-400 font-semibold">{t("Done")}:</span> {doneNames.join(" · ")}
        </div>
      )}
      {steps.length > 0 && (
        <div className="mt-auto flex gap-1">
          {steps.map((step, i) => (
            <span
              key={i}
              className={classNames(
                "flex-1 h-2.5 rounded-sm",
                step.state === "done"
                  ? "bg-emerald-800"
                  : step.state === "current"
                    ? s.step
                    : "bg-gray-800"
              )}
            />
          ))}
        </div>
      )}
      <div className="flex items-center justify-between text-base text-gray-400">
        <span>{t("{{percent}}% stripped", { percent: vehicle.percentComplete })}</span>
        {activeJobUsers.length > 0 ? (
          <div className="flex">
            {activeJobUsers.map((userId, key) => (
              <Avatar
                key={key}
                userId={userId}
                classes="w-8 h-8 rounded-full border-2 border-gray-900 [&:not(:first-child)]:-ml-2"
                textClasses="text-[18px]"
              />
            ))}
          </div>
        ) : (
          <span>{t("No one clocked on")}</span>
        )}
      </div>
    </div>
  );
}

export default function TvBayTile({
  bay,
  assemblies,
  now,
  busyVehicleIds,
  hideNonAssembly = false,
}: TvBayTileProps) {
  const { t } = useTranslation("production");
  const vehicles = bayInProgressVehicles(bay, busyVehicleIds);
  const state = bayState(vehicles, now);
  const s = BAY_STATE_DARK[state];
  const next = bay.dueNext?.[0];

  return (
    <div className={classNames("rounded-2xl border-2 p-5 flex flex-col gap-3 min-h-0", s.card)}>
      <div className="flex items-center justify-between gap-2">
        <span className="text-3xl font-bold leading-none">{bay.name}</span>
        <span
          className={classNames(
            "text-sm font-bold uppercase tracking-widest rounded-md px-2.5 py-1",
            s.pill
          )}
        >
          {t(bayStateLabel(state))}
        </span>
      </div>
      {vehicles.length === 0 ? (
        <div className="flex-1 flex flex-col justify-center gap-3">
          <span className="text-5xl font-semibold leading-none text-gray-400">{t("Bay empty")}</span>
          <span className="text-lg text-gray-300">
            {next
              ? t("Next: {{vehicle}}", {
                  vehicle: [next.vrm, vehicleName(next)].filter(Boolean).join(" "),
                })
              : t("Nothing planned today")}
          </span>
        </div>
      ) : (
        <div className="flex-1 flex flex-col gap-4 divide-y divide-gray-800 min-h-0">
          {vehicles.map((v, i) => (
            <div key={v.id} className={classNames("flex flex-col flex-1 min-h-0", i > 0 && "pt-4")}>
              <TvVehicle
                vehicle={v}
                assemblies={assemblies}
                now={now}
                compact={vehicles.length > 1}
                hideNonAssembly={hideNonAssembly}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
